import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/variables';
import { courseMeta } from '../../data/projects';
import ProjectDetailModal from './ProjectDetailModal';

const ProjectsSection = styled.section`
  padding: 80px 24px;
  background-color: ${colors.white};
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: 16px;
`;

const SectionSubtitle = styled.p`
  font-size: 18px;
  color: ${colors.textGray};
  margin-bottom: 48px;
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
`;

const ProjectCard = styled.button`
  text-align: left;
  font: inherit;
  background-color: ${colors.white};
  border: none;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  height: 100%;
  cursor: pointer;
  padding: 0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12);
  }

  &:focus-visible {
    outline: 2px solid ${colors.secondary};
    outline-offset: 2px;
  }
`;

const ProjectImage = styled.div`
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ProjectContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const ProjectTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: 8px;
`;

const ProjectDescription = styled.p`
  font-size: 16px;
  color: ${colors.textGray};
  margin-bottom: 16px;
`;

const ProjectSkills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
`;

const SkillTag = styled.span`
  display: inline-block;
  background-color: rgba(70, 117, 249, 0.1);
  color: ${colors.secondary};
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
`;

const ExternalLinkRow = styled.div`
  padding: 0 24px 24px;
`;

const ExternalLink = styled.a`
  color: ${colors.secondary};
  font-weight: 600;
  text-decoration: none;
  font-size: 14px;

  &:hover {
    text-decoration: underline;
  }
`;

const CourseProjects = ({ subtitle, projects, courseSlug }) => {
  const [activeProject, setActiveProject] = useState(null);
  const course = courseSlug ? courseMeta[courseSlug] : null;

  return (
    <ProjectsSection>
      <SectionContainer>
        <SectionTitle>실습 프로젝트</SectionTitle>
        <SectionSubtitle>{subtitle}</SectionSubtitle>

        <ProjectsGrid>
          {projects.map((project, index) => {
            const canOpenModal = course && project.slug;

            return (
              <ProjectCard
                as={canOpenModal ? 'button' : 'div'}
                key={index}
                onClick={canOpenModal ? () => setActiveProject(project) : undefined}
              >
                <ProjectImage>
                  <img src={project.image} alt={project.title} />
                </ProjectImage>
                <ProjectContent>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <ProjectDescription>{project.description}</ProjectDescription>
                  <ProjectSkills>
                    {project.skills.map((skill, skillIndex) => (
                      <SkillTag key={skillIndex}>{skill}</SkillTag>
                    ))}
                  </ProjectSkills>
                </ProjectContent>
                {!canOpenModal && project.link && (
                  <ExternalLinkRow>
                    <ExternalLink
                      href={project.link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {project.linkLabel || '프로젝트 보기'}
                    </ExternalLink>
                  </ExternalLinkRow>
                )}
              </ProjectCard>
            );
          })}
        </ProjectsGrid>
      </SectionContainer>

      {activeProject && course && (
        <ProjectDetailModal
          project={activeProject}
          course={course}
          onClose={() => setActiveProject(null)}
        />
      )}
    </ProjectsSection>
  );
};

export default CourseProjects;
